import { FC, memo, ReactNode } from 'react';
import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';

import { cardBrandKey, formatCardExpiry, type CardBrandKey } from '../../api/customerCards/cardHelpers';
import { AmexMark, CardChip, ContactlessMark, MastercardMark, VisaMark } from './CardBrandMarks';

type Props = {
    brand?: string | null;
    last4?: string | null;
    holderName?: string | null;
    expMonth?: number | null;
    expYear?: number | null;
    expired?: boolean;
    /**
     * `small` (40×26) — a thumbnail: brand ground and mark only.
     * `medium` (68×43) — the receipt card list: print pattern, chip and a readable mark.
     * `large` (136×86) — the wallet card: chip, number, holder, valid-thru and mark.
     */
    size?: 'small' | 'medium' | 'large';
    sx?: SxProps<Theme>;
};

const SIZES = {
    small: { width: 40, height: 26, radius: 4 },
    medium: { width: 68, height: 43, radius: 6 },
    large: { width: 136, height: 86, radius: 9 }
} as const;

/** Card grounds are the brands' own colours, fixed in light and dark mode alike. */
const BRAND_GROUND: Record<CardBrandKey, string> = {
    visa: 'linear-gradient(135deg, #2448D8 0%, #1A1F71 62%, #11154F 100%)',
    mastercard: 'linear-gradient(135deg, #48484A 0%, #232325 52%, #0E0E10 100%)',
    amex: 'linear-gradient(135deg, #3B8AD4 0%, #016FD0 55%, #0157A3 100%)',
    diners: 'linear-gradient(135deg, #7C8B9C 0%, #4D5B6C 58%, #333E4B 100%)',
    isracard: 'linear-gradient(135deg, #6F7F95 0%, #46546A 58%, #2F3A4B 100%)',
    other: 'linear-gradient(135deg, #6B7686 0%, #434D5B 58%, #2C3440 100%)'
};

const SHINE = 'linear-gradient(115deg, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0.07) 30%, rgba(255,255,255,0) 46%)';
/** Fine guilloche-like rings from the lower right, as printed card stock has. */
const PATTERN =
    'repeating-radial-gradient(circle at 118% 135%, rgba(255,255,255,0.06) 0 1px, rgba(255,255,255,0) 1px 5px)';

const NEUTRAL_LABEL: Partial<Record<CardBrandKey, string>> = { diners: 'DINERS', isracard: 'ISRACARD' };

const EMBOSSED = {
    color: '#F3F5F9',
    textShadow: '0 1px 0 rgba(0,0,0,0.55), 0 -0.5px 0 rgba(255,255,255,0.25)',
    whiteSpace: 'nowrap',
    lineHeight: 1
} as const;

/** The brand's own short name, for brands we have no mark for. */
const neutralLabel = (key: CardBrandKey, brand?: string | null): string =>
    NEUTRAL_LABEL[key] ||
    String(brand ?? '')
        .trim()
        .split(/\s+/)[0]
        .toUpperCase()
        .slice(0, 10);

const TextMark: FC<{ label: string; fontSize: number }> = ({ label, fontSize }) => (
    <Box
        component="span"
        sx={{
            ...EMBOSSED,
            display: 'block',
            fontFamily: "'Helvetica Neue', Arial, sans-serif",
            fontWeight: 800,
            fontStyle: 'italic',
            fontSize,
            letterSpacing: '0.04em'
        }}
    >
        {label}
    </Box>
);

/**
 * A saved card drawn as a card: the brand's ground, a subtle print pattern and
 * shine, the network mark where the network prints it. Purely presentational
 * and `aria-hidden` — the caller labels the card in text.
 */
const CardTile: FC<Props> = ({ brand, last4, holderName, expMonth, expYear, expired = false, size = 'small', sx }) => {
    const key = cardBrandKey(brand);
    const { width, height, radius } = SIZES[size];
    const large = size === 'large';
    const medium = size === 'medium';
    const small = !large && !medium;
    const digits = (last4 || '').replace(/\D/g, '').slice(-4);

    let mark: ReactNode;
    let markWidth = 0;
    if (key === 'visa') {
        markWidth = large ? 40 : medium ? 30 : 28;
        mark = <VisaMark width={markWidth} />;
    } else if (key === 'mastercard') {
        markWidth = large ? 34 : medium ? 24 : 26;
        mark = <MastercardMark width={markWidth} />;
    } else if (key === 'amex') {
        markWidth = large ? 26 : medium ? 36 : 34;
        mark = <AmexMark width={markWidth} boxed={large} />;
    } else {
        const label = neutralLabel(key, brand);
        markWidth = large ? 44 : medium ? 50 : 32;
        mark = label ? (
            <TextMark label={label} fontSize={large ? 8.5 : medium ? 8 : 6.5} />
        ) : large || medium ? null : (
            <CardChip width={14} />
        );
    }

    return (
        <Box
            aria-hidden
            sx={[
                (theme) => ({
                    position: 'relative',
                    flex: 'none',
                    width,
                    height,
                    borderRadius: `${radius}px`,
                    overflow: 'hidden',
                    boxShadow:
                        theme.palette.mode === 'dark'
                            ? '0 0 0 1px rgba(255,255,255,0.14)'
                            : large
                              ? '0 1px 2px rgba(15,23,42,0.18), 0 4px 10px rgba(15,23,42,0.14)'
                              : medium
                                ? '0 0 0 1px rgba(15,23,42,0.10), 0 1px 3px rgba(15,23,42,0.18)'
                                : '0 0 0 1px rgba(15,23,42,0.10), 0 1px 1px rgba(15,23,42,0.12)',
                    userSelect: 'none'
                }),
                ...(Array.isArray(sx) ? sx : [sx])
            ]}
        >
            {/* The card itself — muted when expired; the ribbon below stays vivid. */}
            <Box
                sx={{
                    position: 'absolute',
                    inset: 0,
                    background: small
                        ? `${SHINE}, ${BRAND_GROUND[key]}`
                        : `${SHINE}, ${PATTERN}, ${BRAND_GROUND[key]}`,
                    filter: expired ? 'saturate(0.45) brightness(0.9)' : undefined
                }}
            >
                {small && (
                    <Box
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}
                    >
                        {mark}
                    </Box>
                )}

                {medium && (
                    <>
                        <Box sx={{ position: 'absolute', left: 7, top: 8 }}>
                            <CardChip width={12} />
                        </Box>
                        <Box sx={{ position: 'absolute', right: 6, bottom: 5 }}>{mark}</Box>
                    </>
                )}

                {large && (
                    <>
                        <Box sx={{ position: 'absolute', left: 12, top: 22 }}>
                            <CardChip width={20} />
                        </Box>
                        {!expired && (
                            <Box sx={{ position: 'absolute', right: 11, top: 9, color: 'rgba(255,255,255,0.8)' }}>
                                <ContactlessMark width={9} />
                            </Box>
                        )}
                        <Box
                            sx={{
                                ...EMBOSSED,
                                position: 'absolute',
                                left: 12,
                                top: 44,
                                fontFamily: "'OCR A Std', 'OCR A Extended', 'Courier New', ui-monospace, monospace",
                                fontWeight: 700,
                                fontSize: 11,
                                letterSpacing: '0.08em'
                            }}
                        >
                            {digits ? `•••• ${digits}` : '•••• ••••'}
                        </Box>
                        <Box
                            sx={{
                                ...EMBOSSED,
                                position: 'absolute',
                                left: 12,
                                top: 59,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                fontFamily: "'Helvetica Neue', Arial, sans-serif"
                            }}
                        >
                            <Box
                                component="span"
                                sx={{ fontSize: 4.5, fontWeight: 700, lineHeight: 1.05, letterSpacing: '0.04em' }}
                            >
                                VALID
                                <br />
                                THRU
                            </Box>
                            <Box
                                component="span"
                                sx={{
                                    fontFamily: "'OCR A Std', 'Courier New', ui-monospace, monospace",
                                    fontSize: 8,
                                    fontWeight: 700,
                                    letterSpacing: '0.04em'
                                }}
                            >
                                {formatCardExpiry({ expMonth, expYear })}
                            </Box>
                        </Box>
                        {!!holderName && (
                            <Box
                                sx={{
                                    ...EMBOSSED,
                                    position: 'absolute',
                                    left: 12,
                                    bottom: 8,
                                    maxWidth: width - 12 - markWidth - 18,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    fontFamily: "'Helvetica Neue', Arial, sans-serif",
                                    fontWeight: 600,
                                    fontSize: 7,
                                    letterSpacing: '0.07em',
                                    textTransform: 'uppercase'
                                }}
                            >
                                {holderName}
                            </Box>
                        )}
                        <Box sx={{ position: 'absolute', right: 9, bottom: 8 }}>{mark}</Box>
                    </>
                )}
            </Box>

            {large && expired && (
                <Box
                    sx={{
                        position: 'absolute',
                        // Centred on the corner's diagonal, clear of both edges.
                        top: 19,
                        right: -26,
                        width: 100,
                        py: '2px',
                        transform: 'rotate(45deg)',
                        bgcolor: '#D32F2F',
                        color: '#FFFFFF',
                        fontFamily: "'Helvetica Neue', Arial, sans-serif",
                        fontSize: 7.5,
                        fontWeight: 800,
                        letterSpacing: '0.14em',
                        lineHeight: 1.2,
                        textAlign: 'center',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.45)'
                    }}
                >
                    EXPIRED
                </Box>
            )}
        </Box>
    );
};

export default memo(CardTile);
